import { Box, Skeleton, useToast } from '@chakra-ui/react';
import React from 'react';

import {
  consts as commonConsts,
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { useGetLenderVerificationCreditFacilitiesQuery } from '../requests/requests.gql.generated';
import { extractDataTapeMapFromRequiredDataTapes } from './extractDataTapeMapFromRequiredDataTapes';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import { Select } from 'packages/common-frontend/components/Select';
import { SelectStyleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetRequiredDataTapesForVerificationQuery } from 'packages/manualDataTapeUploads/requests/requests.gql.generated';

type VerificationCreditFacilityAccordionPanelProps = {
  organizationId: string;
  selectedCreditFacilityId: Util.Maybe<string>;
  selectedReportingDate: Util.FullDateStr | undefined;
  onUpdateCreditFacilityAndReportSelect: ({
    creditFacilityId,
    reportingDate,
  }: {
    creditFacilityId?: Util.Maybe<string>;
    reportingDate?: Util.Maybe<Util.FullDateStr>;
  }) => void;
  isDisabled: boolean;
};
export const VerificationCreditFacilityAccordionPanel = ({
  organizationId,
  selectedCreditFacilityId,
  selectedReportingDate,
  onUpdateCreditFacilityAndReportSelect,
  isDisabled,
}: VerificationCreditFacilityAccordionPanelProps) => {
  const toast = useToast();
  const { overrides } = useVerificationOptions();

  // Explicitly setting this to avoid issues with reporting date and date picker conversions
  // Internal usage expects default TZ=America/New_York
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clientCurrentDate = commonDate.dateToFullDateString(
    new Date(),
    clientTimeZone,
  );

  const {
    data: requiredDataTapesData,
  } = useGetRequiredDataTapesForVerificationQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    skip: !selectedCreditFacilityId,
    variables: {
      creditFacilityId: selectedCreditFacilityId!,
    },
    fetchPolicy: 'network-only',
  });
  const minimumReportingDate =
    requiredDataTapesData &&
    extractDataTapeMapFromRequiredDataTapes(requiredDataTapesData)
      .reportingDate;
  if (
    minimumReportingDate &&
    selectedReportingDate &&
    selectedReportingDate < minimumReportingDate
  ) {
    // Unset to force an explicit decision.
    onUpdateCreditFacilityAndReportSelect({ reportingDate: null });
  }

  const {
    data: creditFacilitiesData,
  } = useGetLenderVerificationCreditFacilitiesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { organizationId },
  });

  if (!creditFacilitiesData) {
    return (
      <Skeleton data-testid="loading-new-verifications-credit-facility-select" />
    );
  }

  // @dani verification - update with actual raw tape types based on bbpl nodes
  // once makarand updates raw nodes to `rawFile` with metadata
  // instead of an output reportType. this is just a placeholder for now
  // const reportTypes: string[] = ['servicerReport'];

  const {
    lenderVerificationCreditFacilities: creditFacilities,
  } = creditFacilitiesData;
  return (
    <Box>
      {!overrides.fixedCreditFacilityId && (
        <Box mb="16px" width="400px">
          <Select
            value={selectedCreditFacilityId}
            options={creditFacilities.map(
              ({ id, slug: creditFacilitySlug }) => ({
                value: id,
                label: commonUtils.creditFacilitySlugToName(creditFacilitySlug),
              }),
            )}
            onChange={(value) => {
              onUpdateCreditFacilityAndReportSelect({
                creditFacilityId: value,
              });
            }}
            placeholderLabel="Select Credit Facility"
            isDisabled={isDisabled}
            width="400px"
          />
        </Box>
      )}
      <Box mb="16px">
        <SelectStyleDatePicker
          tz={clientTimeZone}
          minDate={
            minimumReportingDate
              ? commonDate.fullDateStrToDate(minimumReportingDate)
              : null
          }
          maxDate={commonDate.fullDateStrToDate(
            commonDate.addBusinessDays(
              clientCurrentDate,
              commonConsts.maxFutureBusinessDaysReportingDate,
            ),
          )}
          selectedDate={
            selectedReportingDate
              ? commonDate.fullDateStrToDate(selectedReportingDate)
              : null
          }
          showTimeSelect={false}
          setSelectedDate={(date: Date) => {
            const fullDateStr = commonDate.dateToFullDateString(
              date,
              clientTimeZone,
            );
            onUpdateCreditFacilityAndReportSelect({
              reportingDate: fullDateStr,
            });
          }}
          width="400px"
          placeHolder="Select Reporting Date"
          disabled={!selectedCreditFacilityId}
          showIcon
        />
      </Box>
    </Box>
  );
};
